/**
 * AI calls, the automation (`/ai-calls/automation`): ring R4's page.
 *
 * The automation rings companies from the active campaigns' queues by itself,
 * one call at a time, inside the calling window, and only while both AI calling
 * and the automation's own switch are on (both ship off). This page is where an
 * operator sees what it is doing: the decision it would make now and why, each
 * campaign's verdict, and its last steps. It re-reads itself every 15 seconds
 * while visible (`useAiAutomation.ts`).
 *
 * Managers get the controls here too: the automation's switch, "call the next
 * one now" per campaign, and "stop everything" in the page head, where it can
 * be reached even when the cards below failed to load. The settings that shape
 * the automation (window, gap, caps, test numbers, readiness) stay on the
 * settings page; the head links there.
 */
import { useState } from "react";
import { Bot, Loader2, RefreshCw } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../../lib/auth";
import { Btn, EmptyState, PageHead } from "../../../ui";
import { Note } from "../../settings/panel/fields";
import { AiCallsTabs } from "../AiCallsTabs";
import { AutomationCampaignsCard } from "./AutomationCampaignsCard";
import { AutomationSettingsLink, StopAllButton, StopNote, type StopResult } from "./AutomationControls";
import { AutomationDecisionCard } from "./AutomationDecisionCard";
import { AutomationHistoryCard } from "./AutomationHistoryCard";
import { useAiAutomation } from "./useAiAutomation";

export function AutomationPage({ user }: { user: StaffUser }) {
  const t = useT();
  const canManage = userCanPlatform(user, "manage:crm.calls");
  const { status, error, stale, refresh } = useAiAutomation();
  const [stopResult, setStopResult] = useState<StopResult | null>(null);

  return (
    <div>
      <PageHead
        title={t("aiCalls.title")}
        sub={t("aiCalls.automation.intro")}
        actions={
          <>
            <Btn onClick={() => void refresh()}>
              <RefreshCw className="size-3.5" />
              {t("common.refresh")}
            </Btn>
            {canManage ? <AutomationSettingsLink /> : null}
            {canManage ? (
              <StopAllButton
                onStopped={(result) => {
                  setStopResult(result);
                  void refresh();
                }}
              />
            ) : null}
          </>
        }
      />

      <AiCallsTabs current="automation" />

      <div className="space-y-5">
        {stopResult ? <StopNote result={stopResult} onClose={() => setStopResult(null)} /> : null}
        {stale ? <Note tone="warn">{t("aiCalls.automation.stale")}</Note> : null}

        {status === null && !error ? (
          <div className="crm-card flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : status === null ? (
          <div className="crm-card overflow-hidden p-0">
            <EmptyState icon={Bot} title={t("aiCalls.automation.errorTitle")} body={t("aiCalls.automation.errorBody")} />
          </div>
        ) : (
          <>
            <AutomationDecisionCard status={status} canManage={canManage} onChanged={refresh} />
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
              <AutomationCampaignsCard status={status} canManage={canManage} onChanged={refresh} />
              <AutomationHistoryCard status={status} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
